import { forwardRef, useEffect, useRef } from "react";
import type { Binding, InkStyle, RenderMode, SigilNode } from "../types";

interface Props {
  nodes: SigilNode[];
  stroke: number;
  glow: number;
  symmetry: number;
  tension: number;
  ink: InkStyle;
  binding: Binding;
  mode: RenderMode;
  charge?: number;
  phase?: "normal" | "burst" | "fire";
  labels?: boolean;
}

const COLORS: Record<InkStyle, { stroke: string; glow: string; background: string }> = {
  neon: { stroke: "#a4fbff", glow: "#18cde1", background: "#061014" },
  chalk: { stroke: "#eee9d9", glow: "#c9c1aa", background: "#121110" },
  blood: { stroke: "#a52a36", glow: "#74121f", background: "#11070a" },
  gold: { stroke: "#d7b568", glow: "#8f6728", background: "#0b0908" },
};

export const SigilCanvas = forwardRef<HTMLCanvasElement, Props>(function SigilCanvas({
  nodes, stroke, glow, symmetry, tension, ink, binding, mode, charge = 0, phase = "normal", labels = false,
}, forwardedRef) {
  const localRef = useRef<HTMLCanvasElement | null>(null);
  const frame = useRef(0);
  const particles = useRef(Array.from({ length: 130 }, (_, index) => ({
    angle: index / 130 * Math.PI * 2 + Math.random() * .15,
    speed: .3 + Math.random() * 1.7,
    size: 1 + Math.random() * 3,
    seed: Math.random() * 20,
  })));

  const setRef = (node: HTMLCanvasElement | null) => {
    localRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  useEffect(() => {
    const canvas = localRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    const colors = COLORS[ink];

    const traceGlyph = () => {
      if (!nodes.length) return;
      context.beginPath();
      context.moveTo(nodes[0].x * 640, nodes[0].y * 640);
      if (mode === "freeform" && nodes.length > 2) {
        for (let index = 1; index < nodes.length; index += 1) {
          const current = nodes[index];
          const previous = nodes[index - 1];
          const midpointX = (previous.x + current.x) * 320;
          const midpointY = (previous.y + current.y) * 320;
          const pull = tension * 35 * (index % 2 ? 1 : -1);
          context.quadraticCurveTo(midpointX + pull, midpointY - pull, current.x * 640, current.y * 640);
        }
      } else {
        for (let index = 1; index < nodes.length; index += 1) context.lineTo(nodes[index].x * 640, nodes[index].y * 640);
      }
      context.stroke();
    };

    const drawBinding = (time: number) => {
      if (binding === "none") return;
      context.save(); context.translate(320, 320);
      context.strokeStyle = colors.stroke; context.globalAlpha = .58 + charge / 300;
      context.lineWidth = Math.max(1, stroke * .42);
      context.beginPath(); context.arc(0, 0, 274 + Math.sin(time * .0017) * charge * .025, 0, Math.PI * 2); context.stroke();
      if (binding === "celestial") {
        context.beginPath(); context.arc(0, 0, 254, 0, Math.PI * 2); context.stroke();
        for (let index = 0; index < 12; index += 1) {
          context.save(); context.rotate(index * Math.PI / 6); context.beginPath(); context.arc(0, -264, index % 3 === 0 ? 5 : 2, 0, Math.PI * 2); context.fillStyle = colors.stroke; context.fill(); context.restore();
        }
      }
      if (binding === "runic") {
        for (let index = 0; index < 24; index += 1) {
          context.save(); context.rotate(index * Math.PI / 12); context.beginPath(); context.moveTo(0, -274); context.lineTo(index % 3 ? 3 : 0, index % 3 ? -257 : -247); context.stroke(); context.restore();
        }
      }
      context.restore();
    };

    const draw = (time: number) => {
      context.clearRect(0, 0, 640, 640);
      const radial = context.createRadialGradient(320, 320, 30, 320, 320, 440);
      radial.addColorStop(0, phase === "fire" ? "#35110a" : colors.background);
      radial.addColorStop(.62, colors.background); radial.addColorStop(1, "#020203");
      context.fillStyle = radial; context.fillRect(0, 0, 640, 640);
      context.strokeStyle = "rgba(255,255,255,.025)"; context.lineWidth = 1;
      for (let radius = 82; radius < 320; radius += 42) { context.beginPath(); context.arc(320, 320, radius, 0, Math.PI * 2); context.stroke(); }

      const pulse = 1 + Math.sin(time * .004) * charge * .0008;
      context.save(); context.translate(320, 320); context.scale(pulse, pulse); context.translate(-320, -320);
      context.strokeStyle = colors.stroke; context.lineWidth = stroke; context.lineCap = "round"; context.lineJoin = "round";
      context.shadowColor = colors.glow; context.shadowBlur = glow + charge * .18;
      context.globalAlpha = phase === "fire" ? Math.max(0, 1 - ((time / 1000) % 1.4) / 1.4) : 1;
      drawBinding(time);
      for (let index = 0; index < symmetry; index += 1) {
        context.save(); context.translate(320, 320); context.rotate(index * Math.PI * 2 / symmetry);
        if (index % 2) context.scale(-1, 1);
        context.translate(-320, -320); traceGlyph(); context.restore();
      }
      context.restore();

      if (labels && phase === "normal") {
        context.font = "11px monospace"; context.textAlign = "center"; context.textBaseline = "middle";
        for (const node of nodes) {
          context.fillStyle = colors.background; context.beginPath(); context.arc(node.x * 640, node.y * 640, 9, 0, Math.PI * 2); context.fill();
          context.fillStyle = colors.stroke; context.fillText(node.letter, node.x * 640, node.y * 640 + .5);
        }
      }

      if (phase !== "normal" || charge > 8) {
        const progress = phase === "normal" ? charge / 100 : 1;
        for (const particle of particles.current) {
          const burstDistance = phase === "burst" ? ((time * .18 * particle.speed) % 390) : phase === "fire" ? ((time * .06 * particle.speed) % 300) : 80 + progress * 170 + Math.sin(time * .003 + particle.seed) * 12;
          const x = 320 + Math.cos(particle.angle) * burstDistance;
          const y = 320 + Math.sin(particle.angle) * burstDistance + (phase === "fire" ? (time * .04 % 180) : 0);
          context.fillStyle = phase === "fire" ? `rgba(238,84,28,${.65 * (1 - burstDistance / 420)})` : colors.stroke;
          context.globalAlpha = phase === "normal" ? .15 + progress * .5 : Math.max(0, 1 - burstDistance / 430);
          context.fillRect(x, y, particle.size, particle.size);
        }
        context.globalAlpha = 1;
      }
      const vignette = context.createRadialGradient(320, 320, 180, 320, 320, 420);
      vignette.addColorStop(0, "rgba(0,0,0,0)"); vignette.addColorStop(1, "rgba(0,0,0,.68)");
      context.fillStyle = vignette; context.fillRect(0, 0, 640, 640);
      frame.current = requestAnimationFrame(draw);
    };
    frame.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame.current);
  }, [binding, charge, glow, ink, labels, mode, nodes, phase, stroke, symmetry, tension]);

  return <canvas ref={setRef} width={640} height={640} className="block aspect-square h-auto w-full" aria-label="Procedurally rendered sigil canvas" />;
});
